import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setBreadcrumbs } from "shared/redux/slices/breadcrumbSlice";
import { ToastUtil } from "utilities/toastUtil";
import * as Yup from "yup";

// Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Button, IconButton, Stack, Divider } from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Loading } from "shared/components/ui/Loading";
import { AdminHeader } from "shared/components/common/AdminHeader";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDAutocomplete from "components/MDAutocomplete";

// Redux
import {
  useGetDetailLXOQuery,
  useLazyGetCenterListQuery,
  useUpdateLXOMutation,
  useChangePasswordLXOMutation,
  useSyncHubMutation,
} from "shared/redux/endpoints/admin";

// Utils
import {
  validatePasswordStrength,
  compareArraysByValue,
} from "shared/utils/common";

// UTILITY FUNCTIONS
const transformCentersToSelectOptions = (centers) =>
  centers.map((center) => ({
    title: center.centerName || center.name,
    label: center.centerName || center.name,
    value: center.centerId || center.id,
  }));

//Schema

const centerSchema = Yup.object({
  code: Yup.string().required(),
  name: Yup.string().required(),
  id: Yup.number().required(),
});

const roleSchema = Yup.object({
  roleId: Yup.number().required(),
  name: Yup.string().required(),
});

const userSchema = Yup.object({
  id: Yup.number().required(),
  fullName: Yup.string().required(),
  userCode: Yup.string().required(),
  email: Yup.string().email().required(),
  avatar: Yup.string().nullable(),
  startDate: Yup.date().nullable(),
  isLocked: Yup.boolean().required(),
  role: roleSchema.required(),
  center: centerSchema.required(),
  centers: Yup.array().of(centerSchema).required(),
});

const submissionSchema = Yup.object({
  password: Yup.string()
    .nullable()
    .test("strong-password", "Password is too weak", (value) => {
      if (!value) return true;
      return validatePasswordStrength(value).isValid;
    }),
  confirmPassword: Yup.string()
    .nullable()
    .oneOf([Yup.ref("password"), null], "Passwords do not match"),
  defaultCenterId: Yup.number().nullable(),
  managedCenterIds: Yup.array()
    .of(Yup.number())
    .test(
      "default-in-managed",
      "Default center must be one of the managed centers ",
      function (value) {
        const { defaultCenterId } = this.parent;
        if (!defaultCenterId) return true;
        return value.includes(defaultCenterId);
      }
    ),
});

const validateUserData = (userData) => {
  try {
    userSchema.validateSync(userData, { abortEarly: false });
    return { isValid: true, errors: [] };
  } catch (validationError) {
    return {
      isValid: false,
      errors: validationError.inner.map((err) => ({
        path: err.path,
        message: err.message,
      })),
    };
  }
};

export default function LXODetailPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [searchParams] = useSearchParams();
  const showBtnSyncHub = searchParams.get("sync_hub") === "true";

  // API HOOKS
  const {
    data: detail,
    isLoading: isLoadingLXODetail,
    error: lxoDetailError,
    refetch: refetchLXODetail,
  } = useGetDetailLXOQuery({ id });

  const [triggerSyncWithHub] = useSyncHubMutation();

  const [triggerGetCenterList] = useLazyGetCenterListQuery();
  const [updateLXO, { isLoading: isUpdatingLXO }] = useUpdateLXOMutation();
  const [triggerChangePassword, { isLoading: isChangingPassword }] =
    useChangePasswordLXOMutation();

  // FORM STATE
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // CENTER MANAGEMENT STATE
  const [defaultCenter, setDefaultCenter] = useState(null);
  const [managedCenters, setManagedCenters] = useState([]);
  const [availableCenters, setAvailableCenters] = useState([]);
  const [centerListState, setCenterListState] = useState({
    page: 0,
    size: 20,
    sort: "centerName,asc",
    search: "",
  });
  const [centerPagination, setCenterPagination] = useState({
    totalPage: 0,
    pageSize: 0,
    currentPage: 0,
    totalElement: 0,
  });
  const [isCenterListLoading, setIsCenterListLoading] = useState(false);
  const [isInitialCenterLoad, setIsInitialCenterLoad] = useState(true);

  // FORM HANDLERS
  const handleFormInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((previousState) => !previousState);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((previousState) => !previousState);
  };

  const preventMouseDownDefault = (event) => {
    event.preventDefault();
  };

  // CENTER MANAGEMENT FUNCTIONS
  const loadCenterOptions = async (requestParams) => {
    // Do nothing if already at the last page
    if (
      requestParams.page > 0 &&
      requestParams.page >= centerPagination.totalPage
    ) {
      return;
    }
    // Prevent duplicate API calls
    if (isCenterListLoading) {
      return;
    }

    setIsCenterListLoading(true);

    try {
      const response = await triggerGetCenterList(requestParams);

      if (response.data?.data) {
        const newCenterOptions = transformCentersToSelectOptions(
          response.data.data.data
        );

        // Handle first load vs pagination
        if (requestParams.page === 0) {
          setAvailableCenters(newCenterOptions);
        } else {
          setAvailableCenters((previousCenters) => {
            const existingCenterIds = previousCenters.map(
              (center) => center.value
            );
            const uniqueNewCenters = newCenterOptions.filter(
              (center) => !existingCenterIds.includes(center.value)
            );
            return [...previousCenters, ...uniqueNewCenters];
          });
        }

        // Update state and pagination
        setCenterListState(requestParams);
        const { totalPage, pageSize, currentPage, totalElement } =
          response.data.data;
        setCenterPagination({
          totalPage,
          pageSize,
          currentPage,
          totalElement,
        });
      }
    } catch (error) {
      console.error("❌ Failed to load center options:", error);
      ToastUtil.showToastError("Failed to load center options");
    } finally {
      setIsCenterListLoading(false);
    }
  };

  const handleCenterAutocompleteOnFocus = useCallback(() => {
    if (isInitialCenterLoad) {
      const initialParams = {
        ...centerListState,
        page: 0,
      };
      loadCenterOptions(initialParams);
      setIsInitialCenterLoad(false);
    }
  }, [isInitialCenterLoad, centerListState]);

  const handleManagedCentersChange = useCallback((newSelectedCenters) => {
    if (!newSelectedCenters) {
      setManagedCenters([]);
      return;
    }

    // Remove duplicates based on value
    const uniqueCenters = newSelectedCenters.filter(
      (center, index, array) =>
        index === array.findIndex((item) => item.value === center.value)
    );

    setManagedCenters(uniqueCenters);
  }, []);

  const handleCenterSearchInputChange = useCallback(
    (searchValue, { action }) => {
      if (action === "input-change") {
        const searchParams = {
          ...centerListState,
          page: 0,
          search: searchValue || "",
        };

        loadCenterOptions(searchParams);
      }
    },
    [centerListState]
  );

  const handleCenterMenuScrollToBottom = useCallback(() => {
    if (isCenterListLoading) {
      return;
    }

    if (centerListState.page >= centerPagination.totalPage - 1) {
      return;
    }

    const nextPageParams = {
      ...centerListState,
      page: centerListState.page + 1,
    };

    loadCenterOptions(nextPageParams);
  }, [centerListState, centerPagination.totalPage, isCenterListLoading]);

  const handleFormSubmission = async (event) => {
    event.preventDefault();
    const submissionTasks = [];

    const submissionData = {
      password: formData.password || null,
      confirmPassword: formData.confirmPassword || null,
      defaultCenterId: defaultCenter?.value || null,
      managedCenterIds: managedCenters.map((c) => c.value),
    };

    try {
      await submissionSchema.validate(submissionData, { abortEarly: false });
    } catch (error) {
      error.inner.forEach((err) => ToastUtil.showToastError(err.message));
      return;
    }

    // Check for data changes
    const hasDefaultCenterChanged =
      defaultCenter?.value !== formData.center?.id;

    const formCenterIds = (formData.centers || []).map((c) => c.id).sort();
    const managedCenterIds = managedCenters.map((c) => c.value).sort();

    const haveManagedCentersChanged = !(
      formCenterIds.length === managedCenterIds.length &&
      formCenterIds.every((id, index) => id === managedCenterIds[index])
    );

    // Password change
    if (formData.password) {
      submissionTasks.push(
        triggerChangePassword({ id, password: formData.password })
          .unwrap()
          .then(() => {
            ToastUtil.showToastSuccess("Password changed successfully");
            setFormData((previousData) => ({
              ...previousData,
              password: "",
              confirmPassword: "",
            }));
          })
      );
    }

    // Center updates
    if (hasDefaultCenterChanged || haveManagedCentersChanged) {
      submissionTasks.push(
        updateLXO({
          id,
          centerId: defaultCenter?.value || null,
          centerIds: managedCenters.map((center) => center.value),
        })
          .unwrap()
          .then(() => {
            ToastUtil.showToastSuccess("Information updated successfully");
          })
      );
    }

    if (submissionTasks.length === 0) {
      ToastUtil.showToastInfo("No changes detected to update");
      return;
    }

    try {
      await Promise.all(submissionTasks);
      refetchLXODetail();
    } catch (error) {
      console.error("Form submission failed:", error);
      ToastUtil.showToastError("Update failed. Please try again.");
    }
  };

  const handleSyncWithHubClick = async () => {
    try {
      await triggerSyncWithHub({ id });
      ToastUtil.showToastSuccess("Sync with Hub successfully");
    } catch (error) {
      console.error("Sync with Hub failed:", error);
      ToastUtil.showToastError("Sync with Hub failed. Please try again.");
    }
  };

  const initializeLXODetailData = (lxoData) => {
    if (!lxoData) return;

    const { center, centers } = lxoData;

    setFormData((previousData) => ({
      ...previousData,
      ...lxoData,
    }));

    if (center) {
      setDefaultCenter({
        label: center.name,
        value: center.id,
      });
    }

    if (centers) {
      const transformedCenters = centers.map((centerItem) => ({
        title: centerItem.name,
        label: centerItem.name,
        value: centerItem.id,
      }));
      setManagedCenters(transformedCenters);
    }

    dispatch(setBreadcrumbs({ title: lxoData.fullName }));
  };

  const defaultCenterSelectProps = {
    labelType: "non-sticky",
    labelText: "Default Center",
    options: managedCenters,
    value: defaultCenter,
    autoComplete: false,
    placeholder: "Select default center",
    className: "form-input box-centers",
    name: "lxo-default-center",
    chipClasses: "chip",
    onChange: (selectedOption) => setDefaultCenter(selectedOption),
    isSearchable: true,
    displayChips: true,
  };

  const managedCentersAutocompleteProps = {
    labelType: "non-sticky",
    labelText: "Manage Centers",
    options: availableCenters,
    value: managedCenters,
    autoComplete: false,
    placeholder: "Select managed centers",
    className: "form-input box-centers",
    name: "managedCenters",
    chipClasses: "chip",
    onFocus: handleCenterAutocompleteOnFocus,
    onChange: handleManagedCentersChange,
    onInputChange: handleCenterSearchInputChange,
    onMenuScrollToBottom: handleCenterMenuScrollToBottom,
  };

  useEffect(() => {
    if (detail) {
      const { isValid, errors } = validateUserData(detail);
      if (!isValid) {
        console.warn("❌ Invalid LXO detail:", errors);
        navigate(-1);
        return;
      }
      initializeLXODetailData(detail);
    }
  }, [detail]);

  return (
    <DashboardLayout>
      <AdminHeader />
      <DashboardNavbar light />

      {isLoadingLXODetail ? (
        <MDBox className="container-lxo-management container-lxo-detail">
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height={300}
          >
            <Loading style={{ width: 150, height: 100 }} />
          </MDBox>
        </MDBox>
      ) : (
        <MDBox className="container-lxo-management container-lxo-detail">
          <MDBox className="header-lxo-task">
            <MDBox className="group-title">
              <MDTypography className="title-main">
                LXO Information Detail
              </MDTypography>
            </MDBox>
            {showBtnSyncHub && (
              <Button className="btn btn-sync" onClick={handleSyncWithHubClick}>
                Sync with Hub
              </Button>
            )}
          </MDBox>

          <MDBox className="content-lxo-task">
            <MDBox component="form" onSubmit={handleFormSubmission} noValidate>
              <Divider />

              {/* Basic Information Section */}
              <MDBox sx={{ mb: 4 }}>
                <h3 className="title-section">1. Basic Information</h3>
                <MDBox className="form" display="flex" flexWrap="wrap" gap={2}>
                  <MDBox className="form-input form-input-w50">
                    <MDInput
                      disabled
                      labelType="non-sticky"
                      labelText="Full name"
                      name="fullName"
                      value={formData?.fullName || ""}
                      placeholder="Enter your Full name"
                      className="form-input"
                      fullWidth
                    />
                  </MDBox>

                  <MDBox className="form-input form-input-w50">
                    <MDInput
                      disabled
                      labelType="non-sticky"
                      labelText="User Code"
                      name="userCode"
                      value={formData?.userCode || ""}
                      placeholder="Enter your User Code"
                      className="form-input"
                      fullWidth
                    />
                  </MDBox>

                  <MDBox className="form-input form-input-w50">
                    <MDInput
                      disabled
                      labelType="non-sticky"
                      labelText="Status"
                      name="isLocked"
                      value={formData?.isLocked ? "Locked" : "Active"}
                      className="form-input"
                      fullWidth
                      autoComplete="off"
                    />
                  </MDBox>

                  <MDBox className="form-input form-input-w50">
                    <MDInput
                      disabled
                      labelType="non-sticky"
                      labelText="Email"
                      name="email"
                      value={formData?.email || ""}
                      placeholder="Enter your Email"
                      className="form-input"
                      fullWidth
                      autoComplete="off"
                    />
                  </MDBox>

                  {/* Default Center Selection */}
                  <MDBox className="form-input form-input-w50">
                    <MDSelect {...defaultCenterSelectProps} />
                  </MDBox>

                  {/* Managed Centers Selection */}
                  <MDBox className="form-input form-input-w50">
                    <MDAutocomplete {...managedCentersAutocompleteProps} />
                  </MDBox>
                </MDBox>
              </MDBox>

              <Divider />

              {/* Password Section */}
              <MDBox sx={{ mb: 4 }}>
                <h3 className="title-section">2. Password</h3>
                <MDBox className="form" display="flex" flexWrap="wrap" gap={2}>
                  <MDBox className="form-input form-input-w50">
                    <MDInput
                      labelType="non-sticky"
                      labelText="Password"
                      name="password"
                      autoComplete="new-password"
                      type={showPassword ? "text" : "password"}
                      //value={formData.password}
                      //onChange={handleFormInputChange}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          password: e.target.value,
                        });
                      }}
                      placeholder="Enter your Password"
                      className="form-input"
                      isPassword={true}
                      required
                      fullWidth
                      endAdornment={
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility}
                          onMouseDown={preventMouseDownDefault}
                          edge="end"
                        >
                          {!formData.password ? (
                            <Visibility sx={{ opacity: 0.4 }} />
                          ) : showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      }
                    />
                  </MDBox>

                  <MDBox className="form-input form-input-w50">
                    <MDInput
                      labelType="non-sticky"
                      labelText="Confirm Password"
                      name="confirmPassword"
                      autoComplete="new-password"
                      type={showConfirmPassword ? "text" : "password"}
                      // value={formData.confirmPassword}
                      // onChange={handleFormInputChange}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          confirmPassword: e.target.value,
                        })
                      }
                      placeholder="Enter your Confirm Password"
                      className="form-input"
                      isPassword={true}
                      required
                      fullWidth
                      endAdornment={
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={toggleConfirmPasswordVisibility}
                          onMouseDown={preventMouseDownDefault}
                          edge="end"
                        >
                          {!formData.confirmPassword ? (
                            <Visibility sx={{ opacity: 0.4 }} />
                          ) : showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      }
                    />
                  </MDBox>
                </MDBox>
              </MDBox>

              {/* Action Buttons */}
              <Stack direction="row" justifyContent="flex-end" className="form">
                <Button
                  color="secondary"
                  onClick={() => navigate(-1)}
                  className="btn btn-back"
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  type="submit"
                  className="btn btn-save"
                  disabled={
                    isUpdatingLXO || isChangingPassword || isLoadingLXODetail
                  }
                >
                  Confirm
                </Button>
              </Stack>
            </MDBox>
          </MDBox>
        </MDBox>
      )}
    </DashboardLayout>
  );
}
